'use client'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { signIn, signOut, useSession } from 'next-auth/react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { AppRoutes } from '@/lib/constants/routes'
import { parseAndCheckToken } from '@/app/utilities/authTokenHelpers'

interface UserContextType {
	repId: string | null
	signin: () => Promise<void>
	signout: () => void
	autosignout: () => void	
	isLoading: boolean
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserContextProvider: React.FC<{
	children: React.ReactNode
}> = ({ children }) => {
	const { data: session, status, update } = useSession()
	const router = useRouter()
	const pathName = usePathname()
	const searchParams = useSearchParams()

	// Combine pathname + search params to preserve query parameters in redirects
	const fullPath = useMemo(() => {
		const search = searchParams.toString()
		return search ? `${pathName}?${search}` : pathName
	}, [pathName, searchParams])

	// ── Single lock: isLoading ────────────────────────────────────────
	// Starts true so the route guard shows loader until session resolves.
	const [isLoading, setIsLoading] = useState(true)

	// ── Derived values (synchronous — no timing window) ──────────────────
	const repId = useMemo(() => {
		if (!session?.idToken) return null
		return parseAndCheckToken(session.idToken)?.repId ?? null
	}, [session?.idToken])

	// Used to update local session and redirect to signin page
	const signin = useCallback(async () => {
		await signIn('okta', { redirect: true, callbackUrl: fullPath }, { prompt: 'login' })

	}, [fullPath])

	const signout = useCallback(async () => {
		try {
			setIsLoading(true)
			// Clear storage before signOut so the next user starts fresh
			// (e.g. no stale company selection). This must happen before
			// signOut() which triggers a redirect — after that, code may not run.
			if (typeof window !== 'undefined') {
				window.localStorage.clear()
				window.sessionStorage.clear()
			}
			await signOut({ redirect: true, callbackUrl: AppRoutes.HOME })
		} catch (e) {
			console.error('Error during sign out:', e)
			router.push(AppRoutes.HOME)
		}
	}, [router])


	const autosignout = useCallback(async () => {
		console.debug('UserContextProvider - Auto signing out authenticated user')
		try {
			setIsLoading(true)
			// Clear storage before signOut so the next user starts fresh
			// (e.g. no stale company selection). This must happen before
			if (typeof window !== 'undefined') {
				window.localStorage.clear()
				window.sessionStorage.clear()
			}
			// await signOut({ redirect: true, callbackUrl: AppRoutes.HOME })
			await signOut({ redirect: false })
		} catch (e) {
			console.error('Error during sign out:', e)
			router.push(AppRoutes.HOME)
		} finally {
			setIsLoading(false)
		}
	}, [])

	// ── Refresh: re-update the session ──────────────────────────────────
	const refresh = useCallback(async () => {
		setIsLoading(true)
		try {
			// await update()
			// add fake wait here for now
			await new Promise(resolve => {
				setTimeout(resolve, 200)
			})
		} catch (e) {
			console.error('UserContextProvider - Failed to refresh session:', e)
		} finally {
			setIsLoading(false)
		}
	}, [])

	// ── Effects ──────────────────────────────────────────────────────────

	// Effect: session status change → unlock isLoading
	useEffect(() => {
		if (status === 'authenticated') {
			refresh()
		} else if (status === 'loading') {
			// waiting for authentication status
		} else {
			setIsLoading(false)
		}
	}, [status, refresh])

	// Listen for when the page is visible, if the user switches tabs
    // and makes our tab visible again, re-fetch the session
	 useEffect(() => {
		const visibilityHandler = () => {
			if(document.visibilityState !== "visible") return
			
			if(status !== "authenticated"){
				update()
			}
		}

		window.addEventListener("visibilitychange", visibilityHandler, false)

		return () =>
		window.removeEventListener("visibilitychange", visibilityHandler, false)
	}, [update,status])

	const contextValue = useMemo(
		() => ({
			repId,
			signin,
			signout,
			autosignout,
			isLoading,
		}),
		[repId, signin, signout, autosignout, isLoading]
	)

	return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
}

export const useUserContext = (): UserContextType => {
	const context = useContext(UserContext)
	if (context === undefined) {
		throw new Error('useUserContext must be used within a UserContextProvider')
	}
	return context
}
